import { Formik, Form } from 'formik'
import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, batch } from 'react-redux'
import styled from 'styled-components'
import * as Yup from 'yup'
import '@/utils/validation'
import noop from 'lodash/noop'
import useRole from '@/hooks/use-role'
import CloseButton from '@/components/close-button'
import { CodeInput } from '@/components/code'
import Countdown from '@/components/countdown'
import { PhoneInput } from '@/components/form'
import { H4, ParagraphRegular, Label } from '@/components/text'
import { createModal, useModalize } from '@/components/modalize/mui'
import { toast } from '@/components/toast-notifications'
import {
  changePhoneNewNumber,
  changePhoneNewNumberSilent,
  changePhoneConfirmCodeSilent
} from '@/models/requests/ports'
import { breakpoints, colors } from '@/styles'
import { formatPhone } from '@/utils/format-phone'
import { showWarningModal } from '@/components/warning-modal'
import { Button, BackButton } from '@/components/button'
import PhoneErrorIcon from './phone-error-icon.svg?react'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
  width: 600px;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    padding: 40px 24px;
  }

  ${props =>
    props.isCodeStep &&
    `
      align-items: center;
      padding: 24px 24px 48px;
      width: 540px;

      @media (max-width: ${breakpoints.ipadMiniMax}) {
        width: 100%;
        padding: 32px 24px 48px;
      }
  `}
`

const Title = styled(H4)`
  margin-bottom: 16px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 32px 0 8px;
  }
`

const Description = styled(ParagraphRegular)`
  color: ${colors.grey16};
  margin-bottom: 32px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-bottom: 24px;
  }
`

const PhoneForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
  }
`

const SubmitButton = styled(Button)`
  margin-left: auto;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    margin-left: 0px;
  }
`

const ErrorMessage = styled.div`
  color: ${colors.red64};
  margin-top: 8px;
`

const CodeWrapper = styled.div`
  max-width: 400px;
  text-align: center;

  ${props =>
    props.noBackButton &&
    `
    margin-top: 24px;
  `}
`

const ResendCodeWrapper = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ResendText = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
`

const ResendButton = styled(Label)`
  display: inline;
  font-size: 14px;
  color: ${colors.link};
  cursor: pointer;
`

const BackWrapper = styled.div`
  width: 100%;
  margin-bottom: 8px;
`

const EmailLink = styled.a`
  color: ${colors.link};
  font-weight: 500;
`

const validationSchema = Yup.object().shape({
  phone: Yup.string().phone().required()
})

const VerifyPhoneModal = ({
  initialStep = 'phone',
  phone = '',
  noBackButton = false,
  altBackButton = false,
  showClose = true,
  customFunc = noop,
  history,
  onClose = noop
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()
  const role = useRole()
  const isLandlord = role === 'landlord'
  const [step, setStep] = useState(initialStep)
  const [phoneState, setPhoneState] = useState(phone)
  const [codeSent, setCodeSent] = useState(false)
  const [codeErrorMessage, setCodeErrorMessage] = useState('')

  const onPhoneSubmit = async values => {
    const { response, body } = await changePhoneNewNumberSilent({
      phone: values.phone
    })

    if (response.ok) {
      setPhoneState(values.phone)
      setStep('code')
    } else if (body.message === 'Phone number already exists.') {
      showWarningModal({
        title: t(
          'b.auth.create.phone_already_registered.title',
          'This phone number is already registered.'
        ),
        message: (
          <Trans i18nKey="b.auth.create.phone_already_registered.text">
            If you already have a liv.rent account with this phone number, please log in instead. If
            you are still having trouble, please reach out to us at{' '}
            <EmailLink href="mailto:support@liv.rent">support@liv.rent</EmailLink>.
          </Trans>
        ),
        icon: PhoneErrorIcon,
        button: {
          text: t('b.auth.create.login.button', 'Log In'),
          onClick: async () => {
            await dispatch.auth.logout()
            close()
          }
        },
        secondaryButton: {
          text: t('b.auth.create.try_again.button', 'Try Again')
        }
      })
    } else {
      showWarningModal({
        title: t('b.auth.create.general_error_phone.title', 'Oops! Something went wrong'),
        message: body.message || body.phone,
        icon: PhoneErrorIcon,
        secondaryButton: {
          text: t('b.auth.create.try_again.button', 'Try Again')
        }
      })
    }
  }

  const handleResendCode = async () => {
    const { response } = await changePhoneNewNumber({ phone: phoneState })
    if (response.ok) {
      setCodeErrorMessage('')
      setCodeSent(true)
    }
  }

  const handleContactSupport = () => {
    history.push('/chat/support-room')
    close()
  }

  const onCodeSubmit = async ({ code }) => {
    const { response, body } = await changePhoneConfirmCodeSilent({
      code: code
    })

    if (response.ok) {
      await batch(() => {
        dispatch.profile.update(['phone'], phoneState)
        dispatch.profile.update(['phone_number'], phoneState)
        dispatch.session.update(['session', 'phone_number'], phoneState)
        dispatch.session.update(['session', 'phone_verified_state'], 'passed')
      })
      toast('', {
        title: t('b.verify_phone_modal.edit.success.toast', 'Phone number verified!'),
        type: 'success'
      })
      customFunc()
      close()
    } else if (body.message === 'Invalid code provided') {
      setCodeErrorMessage(
        t('r.payment.edit.invalid_discount_code.error', 'Invalid code. Please try again.')
      )
    }
    onClose()
  }

  const handleClose = () => {
    onClose()
    close()
  }

  const STEPS = {
    phone: {
      title: t('b.verify_phone_modal.edit.phone_verification.title', 'Verify your phone number'),
      description: isLandlord
        ? t(
            'l.verify_phone_modal.edit.phone_verification.description',
            'Before you continue, we ask all landlords to verify their phone number with a verification code. This is to create a trusted and secure marketplace for our users.'
          )
        : t(
            'r.verify_phone_modal.edit.phone_verification.description',
            'Every renter is required to verify their phone before submitting an application. This is to ensure the safety for all renters and landlords.'
          )
    },
    code: {
      title: t('b.verify_phone_modal.edit.code_verification.title', `Verify that it's you`),
      description: (
        <Trans
          i18nKey="b.verify_phone_modal.edit.code_verification.description"
          values={{ phoneNumber: formatPhone(phoneState) }}
          defaults="We sent a verification code to {{ phoneNumber }}. Please enter the code below:"
        />
      )
    }
  }

  const PhoneStep = () => (
    <>
      {showClose && <CloseButton onClick={handleClose} />}
      <Title>{STEPS[step].title}</Title>
      <Description>{STEPS[step].description}</Description>
      <Formik
        initialValues={{ phone: '' }}
        onSubmit={onPhoneSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ values, isSubmitting }) => (
          <PhoneForm>
            <div>
              <PhoneInput
                name="phone"
                placeholder={t(
                  'r.verify_phone_modal.edit.phone_number.placeholder',
                  'Phone number'
                )}
                value={values.phone}
                autoFocus
                required
              />
            </div>
            <SubmitButton
              theme="primary"
              type="submit"
              id="logged-verify-phone-number"
              disabled={isSubmitting}
            >
              {t('b.verify_phone_modal.edit.send_code.button', 'Send Code')}
            </SubmitButton>
          </PhoneForm>
        )}
      </Formik>
    </>
  )

  const CodeStep = () => (
    <>
      {noBackButton ? (
        <CloseButton onClick={close} />
      ) : (
        <BackWrapper>
          <BackButton
            onClick={
              altBackButton
                ? close
                : () => {
                    setCodeErrorMessage('')
                    setCodeSent(false)
                    setPhoneState('')
                    setStep('phone')
                  }
            }
          />
        </BackWrapper>
      )}
      <CodeWrapper noBackButton={noBackButton}>
        <Title>{STEPS[step].title}</Title>
        <Description>{STEPS[step].description}</Description>
        <CodeInput codeLength={4} onSubmit={onCodeSubmit} />
        {codeErrorMessage && (
          <ErrorMessage>
            <Label fontSize="12px" type="small">
              {codeErrorMessage}
            </Label>
          </ErrorMessage>
        )}
        <ResendCodeWrapper>
          {codeSent ? (
            <ResendText>
              <Label fontSize="14px" type="small">
                {t('b.verify_phone_modal.edit.code_resent.text', 'Code resent!')}{' '}
              </Label>
              <Countdown
                text={t(
                  'b.verify_phone_modal.edit.resend_new_code_countdown.text',
                  'You can resend a new code in [current] seconds...'
                )}
                delay={60000}
                onComplete={() => setCodeSent(false)}
              />
            </ResendText>
          ) : (
            <>
              <ResendText>
                <Label fontSize="14px" type="small">
                  {t(
                    'b.verify_phone_modal.edit.did_not_receive_code.text',
                    `Didn't receive a code?`
                  )}{' '}
                </Label>
              </ResendText>
              <ResendText>
                <Label fontSize="14px" type="small">
                  <Trans i18nKey="b.verify_phone_modal.edit.resend_new_code_or_support.button">
                    <ResendButton fontSize="14px" type="small" onClick={handleResendCode}>
                      Send a new code here
                    </ResendButton>{' '}
                    or{' '}
                    <ResendButton fontSize="14px" type="small" onClick={handleContactSupport}>
                      contact support
                    </ResendButton>
                    .
                  </Trans>
                </Label>
              </ResendText>
            </>
          )}
        </ResendCodeWrapper>
      </CodeWrapper>
    </>
  )

  return (
    <Wrapper isCodeStep={step === 'code'}>
      {step === 'phone' && <PhoneStep />}
      {step === 'code' && <CodeStep />}
    </Wrapper>
  )
}

export const show = createModal(VerifyPhoneModal, {
  clickOutside: false,
  showClose: false,
  width: 'auto'
})
